import React, { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Navbar from '../../../../ReusableBlocks/Navbar';
import PartnerCompanyEditForm from '../Components/forms/PartnerCompanyEditForm';
import PartnerCompanyFilterDropdown from '../Components/shared/PartnerCompanyFilterDropdown';
import PartnerPagination from '../Components/shared/PartnerPagination';
import PageHeader from '../Components/shared/PageHeader';
import PartnerCompaniesTable from '../Components/tables/PartnerCompaniesTable';
import { PARTNER_PORTAL_NOT_AUTHORIZED } from '../Constants/partnerRoutes';
import { usePartnerCompanies } from '../Hooks/usePartnerCompanies';
import { usePartnerCompanyFilters } from '../Hooks/usePartnerCompanyFilters';
import { usePartnerCompanyFormOptions } from '../Hooks/usePartnerCompanyFormOptions';
import { PARTNER_TABLE_PAGE_SIZE, usePartnerPagination } from '../Hooks/usePartnerPagination';
import { usePartnerRoleAccess } from '../Hooks/usePartnerRoleAccess';
import PartnerLayout from '../Layout/PartnerLayout';
import {
  getPartnerApiErrorMessage,
  showPartnerActionErrorPopup,
} from '../Utils/partnerApiFeedback';
import {
  buildPartnerCompanyFilterDropdownOptions,
} from '../Utils/partnerCompanyFiltersUtils';

const PartnerCompaniesPage = () => {
  const access = usePartnerRoleAccess();
  const {
    companies,
    loading,
    error,
    saving,
    updateCompany,
    createCompany,
  } = usePartnerCompanies(access.canPerformPAEOperations);
  const {
    partnerAdoptionEngineers,
    tierTypeOptions,
    serviceLevelOptions,
    loading: formOptionsLoading,
  } = usePartnerCompanyFormOptions(access.canPerformPAEOperations);
  const {
    query,
    setQuery,
    typeFilter,
    setTypeFilter,
    paeFilter,
    setPaeFilter,
    tierFilter,
    setTierFilter,
    serviceLevelFilter,
    setServiceLevelFilter,
    typeOptions,
    paeOptions,
    tierOptions,
    serviceLevelFilterOptions,
    filteredCompanies,
    hasActiveFilters,
    clearFilters,
  } = usePartnerCompanyFilters(companies);
  const filterResetKey = `${query}:${typeFilter}:${paeFilter}:${tierFilter}:${serviceLevelFilter}`;
  const {
    paginatedItems: paginatedCompanies,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    setCurrentPage,
  } = usePartnerPagination(filteredCompanies, PARTNER_TABLE_PAGE_SIZE, filterResetKey);
  const [expandedCompanyId, setExpandedCompanyId] = useState(null);
  const [editingCompany, setEditingCompany] = useState(null);
  const [isAddingCompany, setIsAddingCompany] = useState(false);
  const [actionMessage, setActionMessage] = useState('');

  const emptyTableMessage = useMemo(() => {
    if (!companies.length) {
      return 'No partner companies found.';
    }

    if (hasActiveFilters) {
      return 'No companies match your search or filters.';
    }

    return 'No partner companies found.';
  }, [companies.length, hasActiveFilters]);

  const typeDropdownOptions = useMemo(
    () => buildPartnerCompanyFilterDropdownOptions(typeOptions, 'All types'),
    [typeOptions],
  );

  const paeDropdownOptions = useMemo(
    () => buildPartnerCompanyFilterDropdownOptions(paeOptions, 'All PAE'),
    [paeOptions],
  );

  const tierDropdownOptions = useMemo(
    () => buildPartnerCompanyFilterDropdownOptions(tierOptions, 'All tier statuses'),
    [tierOptions],
  );

  const serviceLevelDropdownOptions = useMemo(
    () => buildPartnerCompanyFilterDropdownOptions(serviceLevelFilterOptions, 'All service levels'),
    [serviceLevelFilterOptions],
  );

  const handleToggleExpand = (companyId) => {
    setExpandedCompanyId((current) => (current === companyId ? null : companyId));
  };

  const handleSave = async (formValues) => {
    if (!editingCompany?.id) {
      throw new Error('Unable to update company. Missing company id.');
    }

    setActionMessage('');

    try {
      await updateCompany(editingCompany.id, formValues);
      setEditingCompany(null);
      setActionMessage('Partner company updated successfully.');
    } catch (saveError) {
      const message = getPartnerApiErrorMessage(saveError, 'Failed to update partner company.');
      await showPartnerActionErrorPopup(message);
      throw saveError;
    }
  };

  const handleCreate = async (formValues) => {
    setActionMessage('');

    try {
      await createCompany(formValues);
      setIsAddingCompany(false);
      setActionMessage('Partner company added successfully.');
    } catch (saveError) {
      const message = getPartnerApiErrorMessage(saveError, 'Failed to add partner company.');
      await showPartnerActionErrorPopup(message);
      throw saveError;
    }
  };

  if (!access.canPerformPAEOperations) {
    return <Navigate to={PARTNER_PORTAL_NOT_AUTHORIZED} replace />;
  }

  return (
    <>
      <Navbar activeTab="PARTNER_PORTAL" />
      <PartnerLayout access={access}>
        <PageHeader
          title="Partner Companies"
          description="Partner company directory for PAE operations — CSN, tier, and adoption engineer details."
          rightContent={<span className="psm-queue-view-pill">PAE View</span>}
        />

        {actionMessage ? <p className="partner-form-complete-indicator">{actionMessage}</p> : null}
        {error ? <p className="text-danger">{error}</p> : null}

        <div className="partner-panel-card">
          <div className="partner-filter-toolbar partner-companies-toolbar">
            <label className="partner-companies-search">
              <span className="partner-companies-search-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                  <path
                    d="M10.5 4a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13zm0 2a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zm7.03 11.47-2.77-2.77 1.41-1.41 2.77 2.77-1.41 1.41z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              <input
                className="form-control partner-companies-search-input"
                type="text"
                placeholder="Search companies..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label="Search companies"
              />
              {query.trim() ? (
                <button
                  type="button"
                  className="partner-companies-search-clear"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                >
                  <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                    <path
                      d="M8.4 7 7 8.4 10.6 12 7 15.6 8.4 17 12 13.4 15.6 17 17 15.6 13.4 12 17 8.4 15.6 7 12 10.6 8.4 7z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              ) : null}
            </label>
            <div className="partner-companies-filters">
              <PartnerCompanyFilterDropdown
                label="Type"
                value={typeFilter}
                options={typeDropdownOptions}
                allLabel="All types"
                className="partner-company-filter-dd--type"
                onChange={setTypeFilter}
              />
              <PartnerCompanyFilterDropdown
                label="PAE"
                value={paeFilter}
                options={paeDropdownOptions}
                allLabel="All PAE"
                className="partner-company-filter-dd--pae"
                onChange={setPaeFilter}
              />
              <PartnerCompanyFilterDropdown
                label="Tier Status"
                value={tierFilter}
                options={tierDropdownOptions}
                allLabel="All tier statuses"
                className="partner-company-filter-dd--tier"
                onChange={setTierFilter}
              />
              <PartnerCompanyFilterDropdown
                label="Service Level"
                value={serviceLevelFilter}
                options={serviceLevelDropdownOptions}
                allLabel="All service levels"
                className="partner-company-filter-dd--service-level"
                onChange={setServiceLevelFilter}
              />
              {hasActiveFilters ? (
                <button
                  type="button"
                  className="partner-companies-clear-filters"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              ) : null}
              <div className="partner-companies-toolbar-actions">
                <span className="partner-companies-count">
                  {totalItems.toLocaleString()}
                  {' '}
                  {totalItems === 1 ? 'company' : 'companies'}
                </span>
                <button
                  type="button"
                  className="btn btn-primary partner-add-company-btn"
                  onClick={() => setIsAddingCompany(true)}
                >
                  Add company
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="partner-loading-text">Loading partner companies...</p>
        ) : (
          <>
            <PartnerCompaniesTable
              companies={paginatedCompanies}
              expandedCompanyId={expandedCompanyId}
              emptyMessage={emptyTableMessage}
              onToggleExpand={handleToggleExpand}
              onEdit={setEditingCompany}
            />
            <PartnerPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              startIndex={startIndex}
              endIndex={endIndex}
              itemLabel="companies"
              onPageChange={setCurrentPage}
            />
          </>
        )}

        <PartnerCompanyEditForm
          company={editingCompany}
          open={Boolean(editingCompany)}
          saving={saving}
          partnerAdoptionEngineerOptions={partnerAdoptionEngineers}
          tierTypeOptions={tierTypeOptions}
          serviceLevelOptions={serviceLevelOptions}
          formOptionsLoading={formOptionsLoading}
          onClose={() => setEditingCompany(null)}
          onSave={handleSave}
        />

        <PartnerCompanyEditForm
          open={isAddingCompany}
          mode="create"
          saving={saving}
          partnerAdoptionEngineerOptions={partnerAdoptionEngineers}
          tierTypeOptions={tierTypeOptions}
          serviceLevelOptions={serviceLevelOptions}
          formOptionsLoading={formOptionsLoading}
          onClose={() => setIsAddingCompany(false)}
          onSave={handleCreate}
        />
      </PartnerLayout>
    </>
  );
};

export default PartnerCompaniesPage;
